import * as React from "react";

import { AccordionQuestionnaireSection } from "@teamprevent/common/questionnaires/components/AccordionQuestionnaireSection";
import { getAnswerToQuestionWithId } from "@teamprevent/common/questionnaires/getAnswerToQuestionWithId";
import { RadioButton, RadioButtonQuestion } from "@teamprevent/common/questionnaires/components/questions/RadioButtonQuestion";
import { TextQuestion } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";

import { Segment } from "semantic-ui-react";

import { AnsweredQuestionWithId } from "@teamprevent/common/model";

import { ConsentToReleaseTheCertificateAnswers } from "../../ppq/models";
import { PartialOhCaseDetailModel } from "../../crud/ohCase/model";
import { AssociateOption } from "../../crud/ohCase/components/details/AssociatedManagersEditor";

interface OwnProps {
    index?: number;
    readonly?: boolean;
    questions?: AnsweredQuestionWithId[];
    ohCase?: PartialOhCaseDetailModel;
    lineManagers: AssociateOption[];
    hrRepresentatives: AssociateOption[];
}

export const PpqClinicalNotesConsentSection = ({
    hrRepresentatives,
    lineManagers,
    index,
    ohCase,
    questions,
    readonly }: OwnProps ) =>
{

    if (!index || !ohCase || !questions)
    {
        return null;
    }

    return (<AccordionQuestionnaireSection
        title="Consent to Release the Certificate"
        key={"Completion"}
        id={"Completion"}
        index={index + 2}
        hideNextButton
    >
        <p>
            Please clearly outline the content of your certificate to the employee and then ask them to
            confirm their preference for consent
        </p>
        <p>
            <strong>Note:</strong> Once your certificate has been generated, it will be managed based
            on the choice that you make below:
        </p>
        <RadioButtonQuestion
            hideDivider={!!getAnswerToQuestionWithId(questions, "ScopeOfReport")}
            inline
            readonly={readonly ?? false}
            questionConfig={({ id: "ScopeOfReport", questionText: "Consent for release of certificate" })}
            mandatory
        >
            <RadioButton
                label="I would like to see a copy of my certificate before it is released to HR and my Manager"
                value={ConsentToReleaseTheCertificateAnswers.EmployeeToViewCertificateBeforeRelease} />
            <RadioButton
                label="I am happy for the certificate to be sent to me, HR and my Manager at the same time"
                value={ConsentToReleaseTheCertificateAnswers.ReleaseCertificate} />
            <RadioButton
                // eslint-disable-next-line max-len
                label="Neither of the above (choose this option if any of the email addresses below are wrong or if the employee / manager email address is blank)"
                value={ConsentToReleaseTheCertificateAnswers.AdminToApproveAlternativeChoice} />
        </RadioButtonQuestion>
        {(() => {
            switch (getAnswerToQuestionWithId(questions, "ScopeOfReport")) {
                case ConsentToReleaseTheCertificateAnswers.EmployeeToViewCertificateBeforeRelease:
                    // eslint-disable-next-line max-len
                    return <Segment>If the employee chooses to see the certificate first you must notify them that the certificate will be sent to the Optima Health portal and they will receive an email notification telling them that the certificate is ready. Advise them that they must respond to this within 5 Days or the certificate will be automatically sent to their Manager and HR. <strong>Check the email address of the manager and employee are correct below. If not, or if the employee / manager email address is blank, you must select the third option (Neither of the above)</strong></Segment>;
                case ConsentToReleaseTheCertificateAnswers.ReleaseCertificate:
                    // eslint-disable-next-line max-len
                    return <Segment>Advise the employee that they will receive a copy of the certificate via the Optima Health portal and that they will receive an email notification telling them the certificate is ready. <strong>Check the email address of the manager and employee are correct below. If not, or if the employee / manager email address is blank, you must select the third option (Neither of the above)</strong></Segment>;
                case ConsentToReleaseTheCertificateAnswers.AdminToApproveAlternativeChoice:
                    return (<TextQuestion
                        inline
                        mandatory
                        readonly={readonly ?? false}
                        questionConfig={({ id: "AlternativeConsentChoice", questionText: "Details of Alternative Consent choice" })}
                        multiline />);
                default:
                    return null;
            }
        })()}
        <TextQuestion
            inline
            readonly
            questionConfig={({ id: "EmployeeEmailAddress", questionText: "Employee Email Address" })}
            answer={ohCase?.personalEmailAddress} />
        <TextQuestion
            inline
            readonly
            questionConfig={({ id: "ManagerEmailAddress", questionText: "Manager Email Address" })}
            answer={ohCase?.associatedManagers?.map(am => lineManagers.find(lm => am.id === lm.id)?.email).join(" & ")} />
        {ohCase && ohCase.associatedHrUsers &&
            <TextQuestion
                inline
                readonly
                questionConfig={({ id: "HREmailAddress", questionText: "HR Email Address" })}
                answer={ohCase.associatedHrUsers.map(ahr => hrRepresentatives.find(hr => ahr.id === hr.id)?.email).join(" & ")} />}
    </AccordionQuestionnaireSection>);
};
